'use client';
import { logger } from '@magicschool/logger';
import type { ClientAuthDetails } from './types';

const fetchAdobeClientAuthToken: () => Promise<ClientAuthDetails> = async () => {
  try {
    const response = await fetch('/api/adobe/token');

    if (!response.ok) {
      // close adobe client if the token fetch fails
      window.CCEverywhere.terminate();
      window.adobe = null;
      throw new Error(`Failed to fetch Adobe token: ${response.status} ${response.statusText}`);
    }
    const data: ClientAuthDetails = await response.json();

    return data;
  } catch (error) {
    logger.error('Error fetching Adobe client auth token:', error);
    throw error;
  }
};

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const getAdobeSdk = async (): Promise<any> => {
  if (window.adobe && typeof window.adobe !== 'undefined') return Promise.resolve(window.adobe);

  await _injectScript('https://cc-embed.adobe.com/sdk/v4/CCEverywhere.js');

  if (window.CCEverywhere) {
    // Docs from adobe on the initialize function: https://developer.adobe.com/express/embed-sdk/docs/v4/#initialize
    // These docs don't actually include the clientAuthProvider piece, but it's a real option. Their docs are just out of date
    return window.CCEverywhere.initialize(
      {
        clientId: process.env.NEXT_PUBLIC_ADOBE_CLIENT_ID ?? '',
        appName: 'MagicSchool AI',
      }, // hostInfo
      undefined, // configParams
      undefined, // authOption
      fetchAdobeClientAuthToken, // clientAuthProvider
    )
      .then((adobeSDK) => {
        window.adobe = adobeSDK;
        return adobeSDK;
      })
      .catch((error) => {
        logger.error('Failed to re-initialize Adobe SDK after script load:', error);
        window.adobe = null;
      });
  }
};

/**
 * Load a script dynamically and on-demand!
 *
 * - uses a promise to ensure the script is loaded before resolving.
 * - supports async and defer attributes.
 * - supports module type for ES modules.
 * - defaults to text/javascript for regular scripts.
 *
 * When importing more than one script, this method performs better than `import`.
 *
 * If the script is already present, it resolves immediately with the existing script's promise.
 * This allows multiple parts of the application to wait for the same script without loading it multiple times.
 *
 * this helps us avoid race conditions where the script is already being loaded. This means many places can be waiting for a script to load.
 * @param src
 * @param param1
 * @returns
 */
export const _injectScript = (
  src: string,
  { async, defer, type = 'text/javascript' }: { async?: boolean; defer?: boolean; type?: 'module' | 'text/javascript' } = {},
): Promise<unknown> => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return Promise.resolve();

  const { promise, resolve, reject } = unpackPromise();

  // check if the script is already loaded
  const existingScript: HTMLScriptElement | null = document.querySelector(`script[src="${src}"]`);
  if (existingScript != null) {
    // Script already loaded/loading: ${src}
    // @ts-ignore
    resolve(existingScript._promise);
    return promise;
  }

  const script = document.createElement('script');
  script.src = src;
  script.async = async || false;
  script.defer = defer || false;
  script.type = type;
  document.head.appendChild(script);

  // @ts-expect-error
  // Store the promise on the script element for potential re-reference, avoids race conditions (above)
  script._promise = promise;
  script.onload = () => {
    resolve();
    // @ts-expect-error - Clear the promise reference after loading
    script._promise = null;
  };

  script.onerror = (event: Event | string) => {
    logger.error('Failed to load script:', event);
    reject(new Error(`Failed to load script: ${event.toString()}`));
  };

  script.onabort = () => {
    logger.warn('Script loading was aborted:', src);
  };

  return promise;
};

function unpackPromise() {
  let _resolve: (value?: unknown) => void;
  let _reject: (reason?: unknown) => void;

  const _promise = new Promise<unknown>((resolve, reject) => {
    _resolve = resolve;
    _reject = reject;
  });
  // @ts-ignore - use before?
  return { promise: _promise, resolve: _resolve, reject: _reject };
}
